import React, { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  Platform,
} from 'react-native';
import { MODULE_COLORS } from '@cubemeet/core';
import PlatformIcon from '../common/PlatformIcon';

export type TodoPriority = 'high' | 'medium' | 'low';

export interface AddTodoModalProps {
  visible: boolean;
  onClose: () => void;
  onAddTodo: (title: string, description: string, priority: TodoPriority) => void;
  t?: (key: string, params?: Record<string, any>) => string | undefined;
  initialTitle?: string;
  initialDescription?: string;
  initialPriority?: TodoPriority;
  isSubmitting?: boolean;
  onAddPresetTodos?: (todos: Array<{ title: string; description: string; priority: TodoPriority }>) => void;
  /**
   * 如果为 true，会以内联 / portal 的方式渲染（不使用 React Native Modal）。
   * 若未传且运行在 web 上，则自动使用内联渲染以避免 web Modal 的兼容问题。
   */
  renderAsInline?: boolean;
}

const fallbackTranslations: Record<string, string> = {
  'addTodoModal.title': '添加待办事项',
  'addTodoModal.customAdd.title': '自定义添加',
  'addTodoModal.customAdd.titleLabel': '标题',
  'addTodoModal.customAdd.titlePlaceholder': '输入待办事项标题',
  'addTodoModal.customAdd.descriptionLabel': '描述',
  'addTodoModal.customAdd.descriptionPlaceholder': '输入详细描述（可选）',
  'addTodoModal.customAdd.priorityLabel': '优先级',
  'addTodoModal.priority.high': '高',
  'addTodoModal.priority.medium': '中',
  'addTodoModal.priority.low': '低',
};

const resolveTranslation = (
  t: AddTodoModalProps['t'],
  key: string,
  params?: Record<string, any>,
): string => {
  const translated = t ? t(key, params) : undefined;
  if (translated && translated !== key) {
    return translated;
  }
  return fallbackTranslations[key] || key;
};

const AddTodoModal: React.FC<AddTodoModalProps> = ({
  visible,
  onClose,
  onAddTodo,
  t,
  initialTitle,
  initialDescription,
  initialPriority = 'medium',
  isSubmitting = false,
  onAddPresetTodos: _onAddPresetTodos,
  renderAsInline,
}) => {
  const [todoTitle, setTodoTitle] = useState(initialTitle || '');
  const [todoDescription, setTodoDescription] = useState(initialDescription || '');
  const [todoPriority, setTodoPriority] = useState<TodoPriority>(initialPriority);

  useEffect(() => {
    if (visible) {
      setTodoTitle(initialTitle || '');
      setTodoDescription(initialDescription || '');
      setTodoPriority(initialPriority || 'medium');
    }
  }, [visible, initialTitle, initialDescription, initialPriority]);

  const handleAddTodo = () => {
    const trimmedTitle = todoTitle.trim();
    if (!trimmedTitle) {
      return;
    }
    onAddTodo(trimmedTitle, todoDescription.trim(), todoPriority);
    setTodoTitle('');
    setTodoDescription('');
    setTodoPriority(initialPriority || 'medium');
    onClose();
  };

  const priorityOptions = useMemo(() => ([
    { value: 'high' as TodoPriority, color: '#ef4444' },
    { value: 'medium' as TodoPriority, color: '#f59e0b' },
    { value: 'low' as TodoPriority, color: '#10b981' },
  ]), []);

  // Decide whether to use the native Modal or render inline (web-friendly)
  const useInline = typeof renderAsInline === 'boolean' ? renderAsInline : Platform.OS === 'web';

  const content = (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>{resolveTranslation(t, 'addTodoModal.title')}</Text>
        <View style={styles.headerActions}>
          <TouchableOpacity
            style={[styles.saveButton, (isSubmitting || !todoTitle.trim()) && styles.disabledButton]}
            onPress={handleAddTodo}
            disabled={isSubmitting || !todoTitle.trim()}
          >
            {isSubmitting ? (
              <ActivityIndicator size="small" color={MODULE_COLORS.travel} />
            ) : (
              <PlatformIcon name="check" size={24} color={MODULE_COLORS.travel} family="MaterialIcons" />
            )}
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.closeButton}
            onPress={onClose}
            disabled={isSubmitting}
          >
            <PlatformIcon name="close" size={24} color="#666" />
          </TouchableOpacity>
        </View>
      </View>

      <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>
            {resolveTranslation(t, 'addTodoModal.customAdd.title')}
          </Text>

          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>
              {resolveTranslation(t, 'addTodoModal.customAdd.titleLabel')}
            </Text>
            <TextInput
              style={styles.textInput}
              value={todoTitle}
              onChangeText={setTodoTitle}
              placeholder={resolveTranslation(t, 'addTodoModal.customAdd.titlePlaceholder')}
              placeholderTextColor="#999"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>
              {resolveTranslation(t, 'addTodoModal.customAdd.descriptionLabel')}
            </Text>
            <TextInput
              style={[styles.textInput, styles.textArea]}
              value={todoDescription}
              onChangeText={setTodoDescription}
              placeholder={resolveTranslation(t, 'addTodoModal.customAdd.descriptionPlaceholder')}
              placeholderTextColor="#999"
              multiline
              numberOfLines={3}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>
              {resolveTranslation(t, 'addTodoModal.customAdd.priorityLabel')}
            </Text>
            <View style={styles.prioritySelector}>
              {priorityOptions.map((option) => (
                <TouchableOpacity
                  key={option.value}
                  style={[
                    styles.priorityOption,
                    todoPriority === option.value && styles.priorityOptionSelected,
                    { borderColor: option.color },
                  ]}
                  onPress={() => setTodoPriority(option.value)}
                  disabled={isSubmitting}
                >
                  <Text
                    style={[
                      styles.priorityOptionText,
                      todoPriority === option.value && { color: option.color },
                    ]}
                  >
                    {resolveTranslation(t, `addTodoModal.priority.${option.value}`)}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>
      </ScrollView>
    </View>
  );

  if (useInline) {
    // On web, render inline content inside a portal-like overlay so it's visible above page content
    const overlayStyle: any = {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.45)',
      zIndex: 10000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 16,
    };

    const panelStyle: any = {
      width: 'min(920px, 96%)',
      maxHeight: '90vh',
      borderRadius: 8,
      overflow: 'hidden',
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
      backgroundColor: 'transparent',
    };

    return (
      // @ts-ignore inline web styles
      <View style={overlayStyle}>
        {/* Panel wrapper uses inline styles to ensure web positioning */}
        {/* @ts-ignore */}
        <View style={panelStyle}>{content}</View>
      </View>
    );
  }

  // Native modal path (mobile)
  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      {content}
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1f2937',
  },
  headerActions: {
    flexDirection: 'row',
    gap: 12,
  },
  saveButton: {
    padding: 8,
    borderRadius: 24,
  },
  disabledButton: {
    opacity: 0.5,
  },
  closeButton: {
    padding: 8,
    borderRadius: 24,
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 20,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: 16,
  },
  inputGroup: {
    marginBottom: 16,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 8,
  },
  textInput: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: '#1f2937',
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  prioritySelector: {
    flexDirection: 'row',
    gap: 12,
  },
  priorityOption: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    backgroundColor: '#fff',
  },
  priorityOptionSelected: {
    backgroundColor: '#f8f9fa',
  },
  priorityOptionText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6b7280',
  },
});

export default AddTodoModal;
